import { ApplicationRef, ElementRef, EnvironmentInjector, Injectable, Injector, Renderer2, ViewContainerRef } from '@angular/core';

import { ComponentLoader, ComponentLoaderFactory } from 'ngx-bootstrap/component-loader';
import { PositioningService } from 'ngx-bootstrap/positioning';

/**
 * Lets a modal component be created against the injector of whoever opened it (see
 * {@link U2000_ComponentLoaderFactory}, wired in app.module.ts).
 *
 * Why: ngx-bootstrap instantiates the modal content with `Injector.create({ parent: <loader injector> })`,
 * and that loader injector is the root one. Services provided at a feature (lazy) module level, e.g.
 * `U6511_TransactionPartyService`, were therefore invisible to the modals opened from that module and
 * failed with `NG0201: No provider found`. The previous ngx-modialog-7 implementation resolved them
 * from the caller's injector (`IOpenModalComponentParams.injector`); this restores that behavior
 * centrally instead of requiring explicit `providers` at each of the ~180 call sites.
 *
 * The caller is only "current" for the duration of {@link runWith} (the synchronous
 * `BsModalService.show()` call, which is where the loaders and the content component are created).
 */
@Injectable({ providedIn: 'root' })
export class U2000_ModalCallerInjector {
    private caller: Injector | null = null;

    constructor(readonly rootInjector: Injector) {}

    runWith<T>(caller: Injector | undefined, action: () => T): T {
        const previous = this.caller;
        this.caller = caller ?? previous;
        try {
            return action();
        } finally {
            this.caller = previous;
        }
    }

    /**
     * Injector to give a loader created right now: permanently bound to the current caller (or the
     * root injector outside of {@link runWith}). It must be a fixed snapshot rather than a dynamic
     * lookup — a modal opened from inside another modal would otherwise resolve through the first
     * modal's own element injector, whose parent is this very holder: infinite recursion.
     */
    snapshot(): Injector {
        return this.caller == null ? this.rootInjector : new U2000_CallerBoundInjector(this.caller, this.rootInjector);
    }
}

/** Resolves from the opener's injector first, then from the root injector. */
class U2000_CallerBoundInjector extends Injector {
    private static readonly notFound = {};

    constructor(
        private readonly caller: Injector,
        private readonly rootInjector: Injector,
    ) {
        super();
    }

    get(token: any, notFoundValue?: any, options?: any): any {
        // The caller's own `notFoundValue` must NOT be forwarded: when the modal content asks for a
        // token, Angular's internal `ChainedInjector` passes a special "check only the element
        // injector" sentinel down to us, and an injector chain that receives it (the opener's
        // `lView[INJECTOR]` is itself a `ChainedInjector`) skips its environment injector — which is
        // precisely where a lazy module's providers live. A private sentinel makes the caller do a
        // complete lookup, and only then do we fall back to the root injector.
        const value = this.caller.get(token, U2000_CallerBoundInjector.notFound, options);
        return value !== U2000_CallerBoundInjector.notFound ? value : this.rootInjector.get(token, notFoundValue, options);
    }
}

/** ngx-bootstrap's loader factory, but each loader is bound to the opener of the modal being shown. */
export class U2000_ComponentLoaderFactory extends ComponentLoaderFactory {
    constructor(
        private readonly environmentInjectorRef: EnvironmentInjector,
        private readonly callerInjector: U2000_ModalCallerInjector,
        private readonly positioningService: PositioningService,
        private readonly applicationRef: ApplicationRef,
        private readonly documentRef: Document,
    ) {
        super(environmentInjectorRef, callerInjector.rootInjector, positioningService, applicationRef, documentRef);
    }

    override createLoader<T extends object>(elementRef?: ElementRef, viewContainerRef?: ViewContainerRef, renderer?: Renderer2): ComponentLoader<T> {
        return new ComponentLoader<T>(
            viewContainerRef,
            renderer,
            elementRef,
            this.callerInjector.snapshot(),
            this.environmentInjectorRef,
            this.applicationRef,
            this.positioningService,
            this.documentRef,
        );
    }
}
